import IonIcon from '@reacticons/ionicons'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getFacility } from 'app/state/ducks/facilities/selectors'
import {
  Anchor,
  Breadcrumbs,
  Group,
  Menu,
  Stack
} from '@mantine/core'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ContractType } from 'app/models/common-types'
import {
  loadOneFacility,
} from 'app/state/ducks/facilities/thunks'
import { getDeals } from 'app/state/ducks/deals/selectors'
import { DealParams } from 'app/models/deal-params'
import { loadAllDeals } from 'app/state/ducks/deals/thunks'
import { loadEntities } from 'app/state/ducks/entities/thunks'
import { loadContacts } from 'app/state/ducks/contacts/thunks'
import { loadWireInstructions } from 'app/state/ducks/wire-instructions/thunks'
import { loadAllFacilityAmendments } from 'app/state/ducks/facility-amendments/thunks'
import PrimaryInput from '../buttons/PrimaryInput'
import { loadAllLoans, loadOneLoan } from 'app/state/ducks/loans/thunks'
import InterestPaymentScheduleDetails from './interest-payment-schedule-details'
import { loadNotesByContract } from 'app/state/ducks/notes/thunks'
import LoanHeader from 'app/views/pages/deals-management/loans/loan-header'
import { getLoan } from 'app/state/ducks/loans/selectors'
import SegmentControlUpdated from 'app/views/components/segments/segment-control-updated'
import { PageLayout } from '../layout/page-layout'

export default function InterestPaymentSchedulePage() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()

  const { facilityId, status, loanId, loanStatus } = useParams()
  const [, setOpenEdit] = useState(false)
  const [, setDeal] = useState<DealParams | undefined>(undefined)

  const deals = useSelector(getDeals)
  const facility = useSelector(getFacility)
  const loan = useSelector(getLoan)

  useEffect(() => {
    dispatch(loadEntities())
    dispatch(loadContacts())
    dispatch(loadWireInstructions())
    dispatch(loadAllDeals())
    dispatch(loadAllFacilityAmendments())
    dispatch(loadAllLoans())
  }, [])

  useEffect(() => {
    if (!facilityId) {
      return
    }
    dispatch(loadOneFacility(facilityId, status ?? ''))
    dispatch(loadNotesByContract(facilityId, ContractType.Facilities))
  }, [facilityId, status])

  useEffect(() => {
    dispatch(loadOneLoan(loanId ?? '', loanStatus ?? ''))
  }, [loanId, loanStatus])


  useEffect(() => {
    if (!facility || !deals || !loan) {
      return
    }
    const selectedDeal = deals.find(
      (deal: DealParams) => deal.id === facility.dealId.id
    )
    if (!selectedDeal) {
      return
    }
    setDeal(selectedDeal)
  }, [facility, deals])

  const items = [
    { title: 'Deal Management', action: () => navigate('/dealmanagement') },
    {
      title: 'Facilities',
      action: () => navigate('/dealmanagement/facilities')
    },
    {
      title: facility?.name ?? '',
      action: () =>
        navigate(
          `/dealmanagement/facilities/${facility?.id}/${facility?.status}`
        )
    }
  ].map((item, index) => (
    <Anchor
      onClick={() => item.action()}
      key={index}
      color="#374151"
      fw={500}
      fz="14px"
    >
      {item.title}
    </Anchor>
  ))

  const tabs: Record<string, { route: string; label: string; pageRender: JSX.Element }> = {
    interestPaymentScheduleCurrent: {
      route: `/dealmanagement/facilities/${facilityId}/${status}/${loanId}/${loanStatus}/interest-payment-schedule`,
      label: 'CURRENT',
      pageRender: <InterestPaymentScheduleDetails facilityId={facilityId} tab='current' />
    },
    interestPaymentScheduleHistory: {
      route: `/dealmanagement/facilities/${facilityId}/${status}/${loanId}/${loanStatus}/interest-payment-schedule/history`,
      label: 'HISTORY',
      pageRender: <InterestPaymentScheduleDetails facilityId={facilityId} tab='history' />
    },
  }

  const tab = Object.entries(tabs).find(([, t]) => t.route === location.pathname)?.[0]
    ?? Object.keys(tabs)[0]


  return (
    <PageLayout>
      <Stack w="100%">
        <Breadcrumbs
          separator={<IonIcon name="chevron-forward-outline" />}
          mt="xs"
        >
          {items}
        </Breadcrumbs>
        {loan && (<LoanHeader loan={loan} customTitle='Interest Payment Schedule' showStatusPill={false} />)}

        <Group w="100%" position="apart" className='tabsGapsActivity'>
          <Group position="right">
            <SegmentControlUpdated tabs={tabs} />
          </Group>
          <Group noWrap>
            <Menu shadow="md" width={250} position="bottom" withinPortal>
              <Menu.Target>
                <div style={{ width: 'fit-content' }}>
                  <PrimaryInput w='159px'>
                    Actions
                    <IonIcon name='chevron-down-outline' className='primaryInputChevronDown' />
                  </PrimaryInput>
                </div>
              </Menu.Target>
              <Menu.Dropdown>
                {facility?.status?.toLowerCase() !== 'submitted' ? (
                  <Menu.Item
                    icon={<IonIcon className="action-ion-icon" name='settings-outline' />}
                    onClick={() => setOpenEdit(true)}
                  >
                    Edit First Interest Payment
                  </Menu.Item>
                ) : null}
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
        {tabs[tab].pageRender}
      </Stack>
    </PageLayout>
  )
}
